/*
 * GameMeter.tsx
 * Visual meter component showing remaining guess attempts.
 * Displays squares that fill up as guesses are used.
 */
'use client';

import React, { useRef, useEffect } from 'react';
import { gsap } from 'gsap';

// Styles
import styles from './GameMeter.module.scss';
import classNames from 'classnames/bind';
const cx = classNames.bind(styles);

// Config
import { GUESSES } from '../config/constants';

// Assets
import IncorrectIcon from '@/assets/svg/Game/icon-game-x_02.svg';

// --------------------

interface GameMeterProps {
  guessesUsed: number;
  incorrectGuesses: number[];
  copy?: {
    singular?: string;
    plural?: string;
  };
}

const GameMeter: React.FC<GameMeterProps> = ({ guessesUsed, incorrectGuesses, copy }) => {

  const iconRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const squareRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const animatedIncorrectGuesses = useRef<Set<number>>(new Set());

  // Calculate remaining tiles
  const tilesRemaining = GUESSES - guessesUsed;

  // Get the appropriate copy text (singular or plural)
  const getMeterLabel = () => {
    const template = tilesRemaining === 1 ? copy?.singular : copy?.plural;
    return template?.replace('{{count}}', String(tilesRemaining)) || `${tilesRemaining} tiles left`;
  };

  // Incorrect guess animation (x)
  useEffect(() => {
    const timelines: gsap.core.Timeline[] = [];

    incorrectGuesses.forEach((guessNumber) => {
      // Only animate if we haven't animated this one yet
      if (!animatedIncorrectGuesses.current.has(guessNumber)) {

        // Guys we are animating
        const iconElement = iconRefs.current.get(guessNumber);
        const squareElement = squareRefs.current.get(guessNumber);

        // Icon
        if (iconElement) {
          const tl = gsap.timeline().add('start');

          // Fade in icon
          tl.fromTo(iconElement, {
            opacity: 0,
          }, {
            opacity: 1,
            duration: 0.16
          }, 'start');

          // Scale in icon
          tl.fromTo(iconElement, {
            scale: 4,
          }, {
            scale: 1,
            duration: 0.8,
            ease: 'bounce.out',
          }, 'start');

          // Square
          if (squareElement) {
            tl.add('square-back-normal', 'start+=1');

            // Set bigger and pink border
            tl.set(squareElement, {
              borderWidth: '1.5px',
              borderColor: 'var(--highlight-color)',
              scale: 1.75,
              zIndex: 1,
            }, 'start');

            // Animate back to normal scale
            tl.to(squareElement, {
              borderWidth: '1px',
              scale: 1,
              duration: 0.32,
              ease: 'power1.in',
            }, 'square-back-normal');

            // Set border color back to normal
            tl.set(squareElement, {
              borderColor: 'var(--gray-2)'
            }, 'square-back-normal');
          }

          timelines.push(tl);

          animatedIncorrectGuesses.current.add(guessNumber);
        }
      }
    });

    // Cleanup: kill any running timelines if component unmounts
    return () => {
      timelines.forEach(tl => tl.kill());
    };
  }, [incorrectGuesses]);

  // -------------------- Render

  return (
    <div className={cx('guess-meter')}>
      <div className={cx('guess-meter-squares')}>
        {Array.from({ length: GUESSES }, (_, index) => {
          const guessNumber = index + 1;
          const isUsed = guessNumber <= guessesUsed;
          const isIncorrect = incorrectGuesses.includes(guessNumber);

          // Return guess square
          return (
            <div key={index}
              className={cx('guess-square', {
                'used': isUsed && !isIncorrect,
                'incorrect': isIncorrect
              })}
              ref={(el) => {
                if (el && isIncorrect) {
                  squareRefs.current.set(guessNumber, el);
                }
              }}
            >
              {isIncorrect && (
                <div className={cx('icon-x-wrap')} ref={(el) => {
                  if (el) iconRefs.current.set(guessNumber, el);
                }}>
                  <IncorrectIcon width="100%" height="100%" aria-hidden="true" className={cx('icon-x')} />
                </div>
              )}
            </div>
          );

        })}
      </div>
      <div className={cx('text-s', 'guess-meter-label')}>
        {getMeterLabel()}
      </div>
    </div>
  );

};

export default GameMeter;
